import { useEffect, useMemo, useState } from "react";
import {
    Loader2,
    X,
    Sparkles,
    ChevronRight,
} from "lucide-react";
import {
    getOnboardingState,
    submitPhase2,
    dismissPhase2,
} from "@/lib/api";
import { toast } from "sonner";
import { track } from "@/lib/analytics";

// Phase 2 enums — kept in sync with /app/backend/models.py
const PROPERTY_STAGES = [
    { key: "researching", label: "Just researching" },
    { key: "scheduling_inspection", label: "Scheduling an inspection" },
    { key: "inspection_completed", label: "Inspection completed" },
    { key: "under_contract", label: "Under contract" },
    { key: "negotiating_repairs", label: "Negotiating repairs" },
    { key: "closing_soon", label: "Closing soon" },
    { key: "already_own", label: "Already own the property" },
];
const PROPERTY_TYPES = [
    { key: "single_family", label: "Single-family home" },
    { key: "condo", label: "Condo" },
    { key: "townhome", label: "Townhome" },
    { key: "duplex", label: "Duplex" },
    { key: "multifamily", label: "Multifamily" },
    { key: "commercial", label: "Commercial" },
    { key: "other", label: "Other" },
];
const PROPERTIES_OWNED = [
    { key: "none", label: "None yet" },
    { key: "one", label: "1" },
    { key: "two_to_five", label: "2–5" },
    { key: "six_to_twenty", label: "6–20" },
    { key: "twenty_plus", label: "20+" },
];
const US_STATES = [
    "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
    "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
    "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
    "VA","WA","WV","WI","WY","DC",
];

/**
 * Dismissible Phase-2 profiling card.
 *
 * Mounted on the dashboard — silently no-ops UNLESS:
 *   1. The user has completed (or skipped) Phase 1 onboarding, AND
 *   2. The user has at least one upload (signal of meaningful platform
 *      engagement — gates the prompt per spec), AND
 *   3. Phase 2 is neither completed nor dismissed.
 *
 * Per the conversion-protection rules, this card NEVER mounts on
 * checkout, payment, report, or upload flows — only on `/dashboard`.
 *
 * UX: subtle fade-in via Tailwind `animate-in fade-in slide-in-from-top`
 * (matches the rest of the brand's micro-animations). The user can:
 *   - close the card (dismiss endpoint — never re-shown)
 *   - skip a question (just don't answer it)
 *   - submit a partial answer (any 1+ field → flips phase2_completed)
 */
export default function Phase2Card({ user, onChange }) {
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [answers, setAnswers] = useState({
        property_stage: null,
        property_type: null,
        property_state: null,
        properties_owned: null,
    });
    const [submitting, setSubmitting] = useState(false);
    const [dismissing, setDismissing] = useState(false);

    const isInvestor = user?.user_type === "investor"
        || ["small_investor", "growing_investor", "portfolio_investor",
            "investor"].includes(user?.audience_segment);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await getOnboardingState();
                if (!alive) return;
                setState(r);
            } catch {
                /* silent — don't break dashboard if endpoint fails */
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    const shouldRender = useMemo(() => {
        if (!state) return false;
        if (state.phase2_completed) return false;
        if (state.phase2_dismissed) return false;
        // Gate on "meaningful engagement": at least one upload.
        if ((state.upload_count || 0) < 1) return false;
        return true;
    }, [state]);

    if (loading || !shouldRender) return null;

    const setAnswer = (field, value) => {
        setAnswers((prev) => ({ ...prev, [field]: value }));
        track(`${field}_selected`, { value });
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const payload = { ...answers };
            // If user isn't an investor, never send properties_owned.
            if (!isInvestor) payload.properties_owned = null;
            const r = await submitPhase2(payload);
            track("phase2_completed", {
                property_stage: payload.property_stage,
                property_type: payload.property_type,
                property_state: payload.property_state,
                properties_owned: payload.properties_owned,
                audience_segment: r?.user?.audience_segment,
            });
            if (r?.user?.audience_segment) {
                track("audience_segment_assigned", {
                    segment: r.user.audience_segment,
                });
            }
            toast.success("Thanks — these help us tune your recommendations.");
            setState({ ...state, phase2_completed: true });
            onChange?.(r?.user);
        } catch {
            toast.error("Couldn't save. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDismiss = async () => {
        setDismissing(true);
        try {
            await dismissPhase2();
            track("phase2_dismissed", {});
            setState({ ...state, phase2_dismissed: true });
        } catch {
            toast.error("Couldn't dismiss. Please try again.");
        } finally {
            setDismissing(false);
        }
    };

    const anyAnswered = Object.values(answers).some((v) => v !== null);

    return (
        <section
            className="bl-card p-5 sm:p-6 mb-5 relative animate-in fade-in slide-in-from-top-2 duration-500"
            data-testid="phase2-card"
        >
            <button
                type="button"
                onClick={handleDismiss}
                disabled={dismissing}
                className="absolute top-3 right-3 p-1 rounded text-bl-muted hover:text-bl hover:bg-bl-bg transition-colors"
                aria-label="Dismiss profiling card"
                data-testid="phase2-dismiss-btn"
                title="Don't ask again"
            >
                {dismissing ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                    <X className="h-3.5 w-3.5" />
                )}
            </button>

            <header className="flex items-start gap-2.5 mb-4 pr-8">
                <Sparkles className="h-4 w-4 text-bl-primary mt-0.5 shrink-0" />
                <div>
                    <h2
                        className="font-display font-semibold text-base sm:text-lg text-bl"
                        data-testid="phase2-headline"
                    >
                        Help us improve your recommendations
                    </h2>
                    <p className="text-xs text-bl-muted mt-1">
                        A few optional questions. Skip any you don't want to
                        answer — used only to make Buyer's Leverage better.
                    </p>
                </div>
            </header>

            <div className="space-y-4">
                <Field label="What stage are you in?">
                    <ChipRow
                        options={PROPERTY_STAGES}
                        value={answers.property_stage}
                        onChange={(v) => setAnswer("property_stage", v)}
                        testIdPrefix="phase2-property-stage"
                    />
                </Field>

                <Field label="What type of property is this?">
                    <ChipRow
                        options={PROPERTY_TYPES}
                        value={answers.property_type}
                        onChange={(v) => setAnswer("property_type", v)}
                        testIdPrefix="phase2-property-type"
                    />
                </Field>

                <Field label="Where is the property located? (state, optional)">
                    <select
                        value={answers.property_state || ""}
                        onChange={(e) =>
                            setAnswer("property_state", e.target.value || null)
                        }
                        className="w-full sm:w-auto bg-bl-bg border border-bl rounded px-3 py-2 text-sm transition-colors hover:border-bl-primary/60 focus:border-bl-primary focus:outline-none"
                        data-testid="phase2-property-state-select"
                    >
                        <option value="">— Skip —</option>
                        {US_STATES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </Field>

                {isInvestor && (
                    <Field
                        label="How many investment properties do you currently own?"
                        testId="phase2-investor-question"
                    >
                        <ChipRow
                            options={PROPERTIES_OWNED}
                            value={answers.properties_owned}
                            onChange={(v) => setAnswer("properties_owned", v)}
                            testIdPrefix="phase2-properties-owned"
                        />
                    </Field>
                )}
            </div>

            <div className="mt-5 flex items-center gap-2 flex-wrap">
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting || dismissing || !anyAnswered}
                    className="bl-btn-primary text-xs gap-1.5"
                    data-testid="phase2-save-btn"
                    title={
                        !anyAnswered
                            ? "Answer at least one question, or close to skip"
                            : "Save your answers"
                    }
                >
                    {submitting ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                        <ChevronRight className="h-3 w-3" />
                    )}
                    Save
                </button>
                <button
                    type="button"
                    onClick={handleDismiss}
                    disabled={submitting || dismissing}
                    className="bl-btn-ghost text-xs"
                    data-testid="phase2-skip-btn"
                >
                    Not now
                </button>
                <span className="text-[11px] text-bl-muted ml-auto">
                    You can edit these anytime in Profile Settings.
                </span>
            </div>
        </section>
    );
}

function Field({ label, children, testId }) {
    return (
        <div data-testid={testId}>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-bl-muted mb-2">
                {label}
            </label>
            {children}
        </div>
    );
}

function ChipRow({ options, value, onChange, testIdPrefix }) {
    return (
        <div className="flex flex-wrap gap-1.5">
            {options.map((o) => {
                const selected = value === o.key;
                return (
                    <button
                        key={o.key}
                        type="button"
                        onClick={() => onChange(o.key)}
                        aria-pressed={selected}
                        data-testid={`${testIdPrefix}-${o.key}`}
                        className={
                            "px-2.5 py-1 rounded-full border text-xs sm:text-sm transition-all duration-200 active:scale-95 "
                            + (selected
                                ? "border-bl-primary bg-bl-primary text-white shadow-sm"
                                : "border-bl/40 bg-white text-bl-text hover:border-bl hover:shadow-sm")
                        }
                    >
                        {o.label}
                    </button>
                );
            })}
        </div>
    );
}
